import { Link, useLocation } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { cn } from "@/components/utils/utils";
import { 
  Plane,
  Calendar,
  CreditCard,
  ChevronLeft,
  ChevronRight
} from "lucide-react";
import { useSidebar } from "@/contexts/SidebarContext";

interface SidebarProps {
  isCollapsed: boolean;
  onToggle: () => void;
}

const Sidebar = ({ isCollapsed, onToggle }: SidebarProps) => {
  const location = useLocation();
  const { setMobileSidebarOpen } = useSidebar();

  const menuItems = [
    {
      name: "Trips",
      path: "/dashboard",
      icon: Plane,
      isActive: location.pathname === "/dashboard"
    },
    {
      name: "Booking",
      path: "/booking",
      icon: Calendar,
      isActive: location.pathname === "/booking",
      badge: "Soon"
    },
    {
      name: "Payments",
      path: "/payments",
      icon: CreditCard,
      isActive: location.pathname === "/payments",
      badge: "Soon"
    }
  ];

  const handleMobileClose = () => {
    setMobileSidebarOpen(false);
  };

  return (
    <TooltipProvider delayDuration={0}>
      <div className={cn(
        "flex flex-col bg-white transition-all duration-300 ease-in-out h-full",
        // Desktop: larghezza animata, Mobile: nessun bordo (modal)
        "md:border-r md:border-gray-200",
        isCollapsed ? "md:w-[70px]" : "md:w-[208px]"
      )}>
        {/* Logo Section */}
        <div className="flex items-center justify-between p-4 border-b border-gray-200 h-16">
          <Link to="/dashboard" className="flex items-center justify-start group border border-transparent">
            <div className="transition-all duration-300 ease-in-out">
              {isCollapsed ? (
                <img
                  src="/assets/images/dark_small.svg"
                  alt="Place2Be Logo"
                  className="h-8 w-8 object-contain"
                />
              ) : (
                <img
                  src="/assets/images/dark_extended.svg"
                  alt="Place2Be Logo"
                  className="h-8 w-auto"
                />
              )}
            </div>
          </Link>
        </div>

        {/* Navigation Menu */}
        <nav className="flex-1 py-4 px-3 overflow-y-auto border border-transparent">
          <ul className="space-y-2 border border-transparent">
            {menuItems.map((item) => {
              const Icon = item.icon;
              const menuItem = (
                <Link
                  to={item.path}
                  onClick={handleMobileClose}
                  className={cn(
                    "flex items-center gap-3 px-2 py-2 rounded-lg transition-all duration-200 group border border-transparent h-10",
                    item.isActive
                      ? "bg-gray-100 text-gray-900 border border-gray-200"
                      : "text-gray-600 hover:bg-gray-50 hover:text-gray-900"
                  )}
                >
                  <Icon className={cn(
                    "h-5 w-5 flex-shrink-0 border border-transparent",
                    item.isActive ? "text-gray-700" : "text-gray-500 group-hover:text-gray-700"
                  )} />
                  <div className={cn(
                    "flex items-center gap-2 flex-1 min-w-0 transition-all duration-300 ease-in-out overflow-hidden",
                    isCollapsed ? "w-0 opacity-0" : "w-auto opacity-100"
                  )}>
                    <span className="text-sm font-medium truncate border border-transparent">{item.name}</span>
                    {item.badge && (
                      <Badge variant="default" className="text-xs px-1.5 py-0.5 bg-gray-200 hover:bg-gray-200 text-gray-700">
                        {item.badge}
                      </Badge>
                    )}
                  </div>
                </Link>
              );

              return (
                <li key={item.path} className="border border-transparent">
                  {isCollapsed ? (
                    <Tooltip>
                      <TooltipTrigger asChild>
                        {menuItem}
                      </TooltipTrigger>
                      <TooltipContent side="right" className="flex items-center gap-2">
                        <span>{item.name}</span>
                        {item.badge && (
                          <Badge variant="default" className="text-xs px-1.5 py-0.5 bg-gray-200 hover:bg-gray-200 text-gray-700">
                            {item.badge}
                          </Badge>
                        )}
                      </TooltipContent>
                    </Tooltip>
                  ) : (
                    menuItem
                  )}
                </li>
              );
            })}
          </ul>
        </nav>

        {/* Toggle Button at Bottom - Only on Desktop */}
        <div className="p-4 border-t border-gray-200 justify-end md:flex hidden">
          <Button
            variant="ghost"
            size="sm"
            onClick={onToggle}
            className="h-8 w-8 rounded-full hover:bg-gray-100 transition-all duration-300 ease-in-out"
          >
            <div className="transition-transform duration-300 ease-in-out">
              {isCollapsed ? (
                <ChevronRight className="h-4 w-4" />
              ) : (
                <ChevronLeft className="h-4 w-4" />
              )}
            </div>
          </Button>
        </div>
      </div>
    </TooltipProvider>
  );
};

export default Sidebar;
